import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Image as ImageIcon, Loader2 } from 'lucide-react';
import { LIST_ALL_CLIENT_IMAGES, LIST_ALTERNATED_IMAGES } from '../../../utils';

const getImageUrl = (image) =>
  image?.sourceUrl ||
  image?.SK ||
  image?.url ||
  image?.imageUrl ||
  image?.image;

const getImageTimestamp = (value) => {
  if (!value) return 0;
  if (typeof value === 'number')
    return value < 100000000000 ? value * 1000 : value;
  return new Date(value).getTime() || 0;
};

const isImageMarkedAsUsed = (image) => {
  if (!image) return false;

  const hasUsageTimestamp = (value) => {
    if (value === null || typeof value === 'undefined') return false;
    if (typeof value === 'number') return value > 0;
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) return false;
      if (/^(false|0|null|undefined)$/i.test(trimmedValue)) return false;
      return true;
    }
    return true;
  };

  if (typeof image.isUsed === 'boolean') {
    return image.isUsed;
  }

  if (typeof image.isUsed === 'string') {
    return image.isUsed.trim().toLowerCase() === 'true';
  }

  if (typeof image.isUsed === 'number') {
    return image.isUsed === 1;
  }

  if (hasUsageTimestamp(image.lastUse) || hasUsageTimestamp(image.lastUsed)) {
    return true;
  }

  return false;
};

const UnifiedImageGallery = ({ userId, source, setAppImage }) => {
  const [images, setImages] = useState([]);
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadImages = async () => {
      if (!userId) {
        setImages([]);
        return;
      }

      setLoading(true);
      try {
        const endpoint =
          source === 'image-to-image'
            ? LIST_ALTERNATED_IMAGES
            : LIST_ALL_CLIENT_IMAGES;
        const response = await axios.get(`${endpoint}/${userId}`);
        const responseImages = response.data?.body || response.data || [];
        const normalizedImages = responseImages
          .map((image) => {
            const url = getImageUrl(image);
            return url ? { ...image, url } : null;
          })
          .filter(Boolean)
          .sort((first, second) => {
            const firstDate =
              first.lastUse ||
              first.lastUsed ||
              first.LastModified ||
              first.createdAt ||
              0;
            const secondDate =
              second.lastUse ||
              second.lastUsed ||
              second.LastModified ||
              second.createdAt ||
              0;
            return getImageTimestamp(secondDate) - getImageTimestamp(firstDate);
          });

        if (!cancelled) {
          setImages(normalizedImages);
          setSelectedUrl(null);
        }
      } catch (error) {
        console.error(`Error loading ${source} images:`, error);
        if (!cancelled) setImages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadImages();
    return () => {
      cancelled = true;
    };
  }, [source, userId]);

  const selectImage = (image) => {
    setSelectedUrl(image.url);
    if (image.PK && image.SK) {
      setAppImage({ pk: image.PK, sk: image.SK });
      return;
    }
    setAppImage({ target: { value: image.url } });
  };

  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!images.length) {
    return (
      <div className="min-h-[200px] flex flex-col items-center justify-center gap-2 text-muted-foreground">
        <ImageIcon className="h-10 w-10" />
        <p className="text-base">No photos</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 overflow-y-auto max-h-[1505px]">
      {images.map((image, index) => {
        const isSelected = selectedUrl === image.url;
        const isUsed = isImageMarkedAsUsed(image);
        return (
          <button
            type="button"
            key={`${image.url}-${index}`}
            className={`relative aspect-square overflow-hidden rounded-xl border-2 transition-all ${
              isSelected
                ? 'border-primary ring-2 ring-primary/20'
                : 'border-transparent hover:border-muted-foreground/30'
            }`}
            onClick={() => selectImage(image)}>
            <img
              src={image.url}
              alt={`${
                source === 'image-to-image' ? 'Image variation' : 'Client image'
              } ${index + 1}`}
              className="w-full h-full object-cover"
            />
            {isUsed && (
              <span className="absolute bottom-1.5 left-1.5 bg-muted-foreground/80 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-md">
                Used
              </span>
            )}
            {isSelected && (
              <span className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                <Check className="h-6 w-6 text-primary-foreground bg-primary rounded-full p-1" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default UnifiedImageGallery;
